import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, TrendingUp, TrendingDown, Minus, Zap, Crown, Star, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Avatar } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import FollowButton from '../components/FollowButton';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/leaderboard?period=${period}&limit=50`);
      setLeaderboard(response.data.leaderboard);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLoading(false);
    }
  };

  const getTierBadge = (tier) => {
    const tiers = {
      'Rookie': { icon: Star, color: 'from-gray-600 to-gray-700', text: 'Rookie' },
      'Rising Star': { icon: TrendingUp, color: 'from-blue-600 to-blue-700', text: 'Rising Star' },
      'Featured Creator': { icon: Award, color: 'from-purple-600 to-purple-700', text: 'Featured' },
      'Trap Star': { icon: Crown, color: 'from-orange-500 to-red-500', text: 'Trap Star' }
    };
    return tiers[tier] || tiers['Rookie'];
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: '🥇', color: 'from-yellow-400 to-yellow-600', size: 'text-3xl' };
    if (rank === 2) return { icon: '🥈', color: 'from-gray-400 to-gray-500', size: 'text-2xl' };
    if (rank === 3) return { icon: '🥉', color: 'from-orange-600 to-orange-700', size: 'text-2xl' };
    return { icon: rank, color: 'from-gray-700 to-gray-800', size: 'text-xl' };
  };

  const getChangeIndicator = (change) => {
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const formatEnergy = (energy) => {
    if (energy >= 1000000) return `${(energy / 1000000).toFixed(1)}M`;
    if (energy >= 1000) return `${(energy / 1000).toFixed(1)}K`;
    return energy.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-orange-500/20 to-black border-b border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mb-4">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black mb-4">
              Trap Energy Leaderboard
            </h1>
            <p className="text-lg text-gray-300 mb-6">
              Top creators ranked by community engagement and content drops
            </p>
            
            {/* Period Selector */}
            <div className="flex justify-center gap-2">
              <Button
                onClick={() => setPeriod('week')}
                variant={period === 'week' ? 'default' : 'outline'}
                className={period === 'week' ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'border-gray-700'}
              >
                This Week
              </Button>
              <Button
                onClick={() => setPeriod('month')}
                variant={period === 'month' ? 'default' : 'outline'}
                className={period === 'month' ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'border-gray-700'}
              >
                This Month
              </Button>
              <Button
                onClick={() => setPeriod('all')}
                variant={period === 'all' ? 'default' : 'outline'}
                className={period === 'all' ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'border-gray-700'}
              >
                All Time
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {/* 2nd Place */}
            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 md:order-1">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-3">🥈</div>
                <Link to={`/u/${leaderboard[1].handle}`}>
                  <Avatar className="w-20 h-20 mx-auto mb-3 ring-4 ring-gray-400">
                    {leaderboard[1].avatar_url ? (
                      <img src={leaderboard[1].avatar_url} alt={leaderboard[1].display_name} />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full bg-gray-700 text-2xl">
                        {leaderboard[1].display_name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Avatar>
                </Link>
                <h3 className="font-bold text-lg mb-1">{leaderboard[1].display_name}</h3>
                <p className="text-gray-400 text-sm mb-2">@{leaderboard[1].handle}</p>
                <div className="flex items-center justify-center gap-1 text-orange-500 font-bold mb-3">
                  <Zap className="w-5 h-5" />
                  {formatEnergy(leaderboard[1].trap_energy)}
                </div>
                <FollowButton targetUserId={leaderboard[1].user_id} targetHandle={leaderboard[1].handle} size="sm" />
              </CardContent>
            </Card>

            {/* 1st Place */}
            <Card className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 border-yellow-500/50 md:order-2 md:-mt-4">
              <CardContent className="p-6 text-center">
                <div className="text-5xl mb-3">🥇</div>
                <div className="inline-block relative mb-3">
                  <Link to={`/u/${leaderboard[0].handle}`}>
                    <Avatar className="w-24 h-24 ring-4 ring-yellow-400">
                      {leaderboard[0].avatar_url ? (
                        <img src={leaderboard[0].avatar_url} alt={leaderboard[0].display_name} />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full bg-gray-700 text-3xl">
                          {leaderboard[0].display_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </Avatar>
                  </Link>
                  <Crown className="absolute -top-2 -right-2 w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="font-bold text-xl mb-1">{leaderboard[0].display_name}</h3>
                <p className="text-gray-400 text-sm mb-2">@{leaderboard[0].handle}</p>
                <div className="flex items-center justify-center gap-1 text-orange-500 font-bold text-lg mb-3">
                  <Zap className="w-6 h-6" />
                  {formatEnergy(leaderboard[0].trap_energy)}
                </div>
                <FollowButton targetUserId={leaderboard[0].user_id} targetHandle={leaderboard[0].handle} size="sm" />
              </CardContent>
            </Card>

            {/* 3rd Place */}
            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 md:order-3">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-3">🥉</div>
                <Link to={`/u/${leaderboard[2].handle}`}>
                  <Avatar className="w-20 h-20 mx-auto mb-3 ring-4 ring-orange-600">
                    {leaderboard[2].avatar_url ? (
                      <img src={leaderboard[2].avatar_url} alt={leaderboard[2].display_name} />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full bg-gray-700 text-2xl">
                        {leaderboard[2].display_name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Avatar>
                </Link>
                <h3 className="font-bold text-lg mb-1">{leaderboard[2].display_name}</h3>
                <p className="text-gray-400 text-sm mb-2">@{leaderboard[2].handle}</p>
                <div className="flex items-center justify-center gap-1 text-orange-500 font-bold mb-3">
                  <Zap className="w-5 h-5" />
                  {formatEnergy(leaderboard[2].trap_energy)}
                </div>
                <FollowButton targetUserId={leaderboard[2].user_id} targetHandle={leaderboard[2].handle} size="sm" />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rest of Rankings */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-0">
            <div className="divide-y divide-gray-800">
              {leaderboard.slice(3).map((creator) => {
                const rankBadge = getRankBadge(creator.rank);
                const tierBadge = getTierBadge(creator.creator_tier);
                const TierIcon = tierBadge.icon;

                return (
                  <div key={creator.user_id} className="p-4 hover:bg-gray-800/50 transition-colors">
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br ${rankBadge.color} rounded-lg font-black ${rankBadge.size}`}>
                        {rankBadge.icon}
                      </div>

                      {/* Avatar */}
                      <Link to={`/u/${creator.handle}`}>
                        <Avatar className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-800">
                          {creator.avatar_url ? (
                            <img src={creator.avatar_url} alt={creator.display_name} />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full text-gray-400 text-lg">
                              {creator.display_name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </Avatar>
                      </Link>

                      {/* Creator Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Link to={`/u/${creator.handle}`} className="font-bold text-white hover:text-orange-500 transition-colors truncate">
                            {creator.display_name}
                          </Link>
                          {creator.creator_tier && (
                            <Badge className={`bg-gradient-to-r ${tierBadge.color} text-white text-xs hidden sm:inline-flex`}>
                              <TierIcon className="w-3 h-3 mr-1" />
                              {tierBadge.text}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <span className="truncate">@{creator.handle}</span>
                          {creator.change !== 0 && (
                            <>
                              <span>•</span>
                              {getChangeIndicator(creator.change)}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Trap Energy */}
                      <div className="flex-shrink-0 text-right">
                        <div className="flex items-center gap-1 text-orange-500 font-bold mb-1">
                          <Zap className="w-4 h-4" />
                          <span>{formatEnergy(creator.trap_energy)}</span>
                        </div>
                        <div className="hidden sm:block">
                          <FollowButton targetUserId={creator.user_id} targetHandle={creator.handle} size="sm" variant="outline" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/20 mt-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Zap className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg mb-2">What is Trap Energy?</h3>
                <p className="text-gray-300 text-sm">
                  Trap Energy is earned by creating content, engaging with fans, and growing your community. 
                  Upload comics (+10), artwork (+5), music (+15), and interact with your audience to climb the leaderboard and unlock creator tiers!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Leaderboard;
